import { Flag } from '../components/shared/Flag';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

/** Formato básico de correo: algo@algo.algo, sin espacios. Evita enviar texto suelto como correo. */
export function isValidLoginEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
import { Button, Input, Text, Spinner, Divider, makeStyles, tokens } from '@fluentui/react-components';
import { PhoneInput } from '../components/shared/PhoneInput';
import { NewPasswordFields, PasswordField } from '../components/shared/PasswordField';
import { GoogleSignInButton, googleDisableAutoSelect } from '../components/auth/GoogleSignInButton';
import { buildGoogleNotRegisteredMessage } from '../utils/googleLogin';
import { isValidNewPassword, isValidPhoneE164, isValidResetTarget, normalizePhoneInput } from '../utils/validators';
import { requestOtp, verifyOtp, loginWithPassword, loginWithEmail, requestPasswordReset, resetPassword } from '../api/auth.api';
import { getTenantConfig, type TenantConfig } from '../api/tenant.api';
import { updateMyPreferences } from '../api/settings.api';
import { useAuthStore } from '../store/auth.store';
import { tenantCodeFromHost, apexUrl } from '../utils/host-tenant';
import { useTenantStore } from '../store/tenant.store';
import { useThemeStore } from '../store/theme.store';
import { useLocaleStore, type Locale } from '../store/locale.store';
import { LOCALE_OPTIONS, useT } from '../i18n';
import { SiteFooter } from '../components/layout/SiteFooter';
import { usePageTitle } from '../hooks/usePageTitle';
import type { AuthUser } from '../types/api.types';
import { resolveSafeNext } from '../utils/returnTo';

/**
 * Aplica las preferencias persistidas del usuario (idioma/tema) a los stores
 * globales tras un login exitoso. Solo sobreescribe si el backend envía un
 * valor definido — nunca pisa el default local si el usuario nunca configuró
 * nada (ver PUT /users/me/preferences).
 *
 * `skipLanguage` evita pisar el idioma que el usuario acaba de elegir a mano
 * en esta misma pantalla de login (antes, elegir "EN" y loguearse con una
 * cuenta cuya preferencia guardada era "es" revertía la UI a español justo
 * al entrar — el idioma elegido nunca se persistía de vuelta al backend).
 */
function applyUserPreferences(user: AuthUser, skipLanguage: boolean) {
  const prefs = user.preferences;
  if (!prefs) return;
  if (prefs.theme === 'light' || prefs.theme === 'dark') {
    useThemeStore.getState().setMode(prefs.theme);
  }
  if (!skipLanguage && (prefs.language === 'es' || prefs.language === 'en' || prefs.language === 'zh')) {
    useLocaleStore.getState().setLocale(prefs.language as Locale);
  }
}

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    padding: '24px 0',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: '40px',
    width: '360px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    boxShadow: tokens.shadow16,
  },
  title: { textAlign: 'center' },
  error: { color: tokens.colorStatusDangerForeground1, fontSize: '13px', textAlign: 'center' },
  otpRow: { display: 'flex', gap: '8px' },
});

type Step = 'google' | 'otp' | 'password' | 'password-reset';
type PasswordResetStep = 'method' | 'code' | 'new-password';

export function LoginPage() {
  return (
    <div>
      <LoginPageContent />
      <SiteFooter />
    </div>
  );
}

function LoginPageContent() {
  const s = useStyles();
  const t = useT();
  usePageTitle(t.pageTitle.login);
  const navigate = useNavigate();
  const { tenantId: tenantIdFromPath } = useParams<{ tenantId?: string }>();
  const { setSession } = useAuthStore();
  const { setTenant } = useTenantStore();
  const { locale, setLocale } = useLocaleStore();
  // El usuario tocó el selector de idioma en esta visita a /login — evita que
  // applyUserPreferences() lo revierta al valor guardado en el backend.
  const localeTouchedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>('google');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [pwPhone, setPwPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginEmailPassword, setLoginEmailPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
  const [tenantError, setTenantError] = useState(false);

  // Password reset flow states
  const [resetStep, setResetStep] = useState<PasswordResetStep>('method');
  const [resendIn, setResendIn] = useState(0);        // segundos que faltan para poder reenviar el código
  const [resetLocked, setResetLocked] = useState(false); // se agotaron los envíos: lo desbloquea un administrador
  const [otpWaitIn, setOtpWaitIn] = useState(0);        // espera para pedir otro código de login
  const [otpLocked, setOtpLocked] = useState(false);    // login bloqueado por demasiados códigos
  const [resetPhone, setResetPhone] = useState('');
  const [resetMethod, setResetMethod] = useState<'telegram' | 'email'>('telegram');
  const [resetCode, setResetCode] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetTouched, setResetTouched] = useState(false);
  const [resetConfirmValue, setResetConfirmValue] = useState('');
  const [googleRetryKey, setGoogleRetryKey] = useState(0);
  const [googleRejected, setGoogleRejected] = useState(false);

  // Cuenta regresiva de la espera para reenviar el código.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn(n => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  useEffect(() => {
    if (otpWaitIn <= 0) return;
    const id = window.setTimeout(() => setOtpWaitIn(n => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [otpWaitIn]);

  /** Límite progresivo de OTP de login (429 con otpThrottled). Devuelve true si lo manejó. */
  const handleOtpThrottle = (e: any): boolean => {
    const d = e?.response?.data;
    if (e?.response?.status !== 429 || !d?.otpThrottled) return false;
    setOtpLocked(!!d.locked);
    setOtpWaitIn(d.locked ? 0 : Number(d.retryAfterSeconds) || 0);
    setError('');
    return true;
  };

  const resetValid = isValidResetTarget(resetMethod, resetPhone);
  const resetPasswordOk = isValidNewPassword(resetPasswordValue, resetConfirmValue, tenantConfig?.passwordMinLength);
  const changeResetMethod = (m: 'telegram' | 'email') => {
    if (m === resetMethod) return;
    setResetMethod(m);
    setResetPhone('');
    setResetTouched(false);
    setError('');
  };

  // Get invite token from query param
  const inviteToken = searchParams.get('invite');

  // Get tenantId from the route path (/:tenantId/login), query param (?tenant=),
  // or localStorage — en ese orden de prioridad. El path param es el caso normal
  // hoy que las URLs siempre llevan el code del tenant (ver /:tenantId/login en App.tsx).
  const tenantIdParam = searchParams.get('tenant');
  const tenantIdFromStorage = (() => {
    try {
      return sessionStorage.getItem('agrovid_tenant') || localStorage.getItem('agrovid_tenant');
    } catch {
      return null;
    }
  })();
  const tenantId = tenantIdFromPath || tenantIdParam || tenantIdFromStorage;

  // Fetch tenant config if tenantId available
  useEffect(() => {
    if (!tenantId) return;
    getTenantConfig(tenantId)
      .then(setTenantConfig)
      .catch((err) => {
        // Subdominio que no es una empresa (algo.agrovid.solutions): se manda a la raíz.
        // Solo con un 404 real; un fallo de red no debe sacar al usuario de su empresa.
        const apex = tenantCodeFromHost() ? apexUrl() : null;
        if (apex && err?.response?.status === 404) {
          window.location.replace(apex);
          return;
        }
        setTenantError(true);
      });
  }, [tenantId]);

  // F0.10 — los links repartidos antes apuntan a `/login?invite=<token>`, donde
  // solo había un banner informativo. Ahora se redirige a la pantalla que sí
  // permite completar el alta.
  useEffect(() => {
    if (!inviteToken) return;
    navigate(`/accept-invite/${inviteToken}`, { replace: true });
  }, [inviteToken, navigate]);

  const handleDemo = () => {
    navigate('/demo/login', { replace: true });
  };

  /** Completa el login tras obtener token/user — usado por OTP, password y bypass de OTP. */
  const completeLogin = (res: { token: string; user: AuthUser; needsRoleSelection?: boolean }) => {
    setSession(res.token, res.user);
    applyUserPreferences(res.user, localeTouchedRef.current);
    if (localeTouchedRef.current) {
      // El usuario eligió idioma a mano en esta pantalla — persistirlo para
      // que el próximo login (que sí respeta el valor guardado) no lo pierda.
      const { locale: chosenLocale } = useLocaleStore.getState();
      const { mode: currentTheme } = useThemeStore.getState();
      void updateMyPreferences({ language: chosenLocale, theme: currentTheme }).catch(() => { /* best-effort */ });
    }
    // El code (slug de URL, ej. "empresa1") viene del login — más confiable
    // que tenantConfig, que depende de un ?tenant= que puede no estar presente.
    const destCode = res.user.tenantCode ?? tenantId ?? res.user.tenantId ?? 'demo';
    setTenant(destCode, res.user.tenantName ?? tenantConfig?.displayName ?? destCode, res.user.tenantLogoUrl ?? tenantConfig?.logoUrl ?? undefined);
    const safeNext = resolveSafeNext(searchParams.get('next'), destCode);
    if (res.needsRoleSelection) {
      const q = safeNext ? `?next=${encodeURIComponent(safeNext)}` : '';
      navigate(`/${destCode}/select-role${q}`, { replace: true });
    } else {
      navigate(safeNext ?? `/${destCode}/dashboard`, { replace: true });
    }
  };

  const handleGoogle = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) return;
    setLoading(true);
    setError('');
    setGoogleRejected(false);
    setOtpLocked(false); setOtpWaitIn(0);
    try {
      const res = await requestOtp(credentialResponse.credential, tenantId ?? undefined);
      // El tenant puede tener requireOtp=false — el backend hace bypass y
      // devuelve el JWT directo en vez de pedir código por Telegram.
      if ('token' in res) {
        completeLogin(res);
        return;
      }
      setPhone(res.phoneNumber);
      setStep('otp');
    } catch (e: any) {
      if (handleOtpThrottle(e)) return;
      // F0.10 — la cuenta existe pero no tiene Telegram vinculado: el código no
      // tiene por dónde llegar. El backend responde 409 TELEGRAM_NOT_LINKED en
      // vez de fingir que lo mandó; aquí se dice qué hacer.
      if (e?.response?.data?.error === 'TELEGRAM_NOT_LINKED') {
        const canUsePassword = e.response.data.canUsePassword === true;
        setError(
          `${t.auth.telegramNotLinked} ${t.auth.telegramNotLinkedHint}` +
          (canUsePassword ? ` ${t.auth.telegramNotLinkedPassword}` : ''),
        );
        return;
      }
      const backendMsg: string | undefined = e?.response?.data?.error;
      if (e?.response?.status === 403) {
        // Correo no registrado: mostrar cuál correo falló (decodificado en cliente, solo para UI).
        setGoogleRejected(true);
        setError(buildGoogleNotRegisteredMessage(
          credentialResponse.credential,
          t.auth.googleEmailNotRegistered,
          backendMsg ?? t.auth.errorGoogle,
        ));
        return;
      }
      setError(backendMsg ?? t.auth.errorGoogle);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setLoading(true);
    setError('');
    setOtpLocked(false); setOtpWaitIn(0);
    try {
      const res = await loginWithPassword(normalizePhoneInput(pwPhone), password, tenantId ?? undefined);
      // El tenant exige doble factor — continuar con el mismo paso de OTP
      // que usa el flujo de Google, en vez de emitir sesión directa.
      if (res.requiresOtp) {
        setPhone(res.phoneNumber);
        setStep('otp');
        return;
      }
      completeLogin(res);
    } catch (e: any) {
      if (handleOtpThrottle(e)) return;
      const msg = e?.response?.data?.message ?? e?.response?.data?.error ?? t.auth.errorPassword;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  /** Correo y contraseña dentro de la empresa (POST /api/auth/login). Fallos genéricos. */
  const handleEmailLogin = async () => {
    if (!isValidLoginEmail(loginEmail)) {
      setError('Escribe un correo válido, por ejemplo nombre@dominio.com.');
      return;
    }
    setLoading(true);
    setError('');
    setOtpLocked(false); setOtpWaitIn(0);
    try {
      const res = await loginWithEmail(tenantId ?? '', loginEmail.trim(), loginEmailPassword);
      if (res.requiresOtp) {
        setPhone(res.phoneNumber);
        setStep('otp');
        return;
      }
      completeLogin(res);
    } catch (e: any) {
      if (handleOtpThrottle(e)) return;
      setError(e?.response?.data?.error ?? t.auth.errorPassword);
    } finally {
      setLoading(false);
    }
  };

  const handleOtp = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await verifyOtp(phone, otp);
      completeLogin(res);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? t.auth.errorOtp);
    } finally {
      setLoading(false);
    }
  };

  /** Pide (o reenvía) el código. El servidor impone una espera creciente y bloquea a las 5 peticiones. */
  const handlePasswordResetRequest = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await requestPasswordReset(resetMethod === 'email' ? resetPhone.trim() : normalizePhoneInput(resetPhone), resetMethod);
      setResendIn(res.retryAfterSeconds ?? 0);
      setResetLocked(!!res.locked);
      if (res.locked && res.sent === false) {
        // Ya estaba bloqueado: no se envió nada, solo se avisa.
        setError(t.auth.resetLocked);
        return;
      }
      setResetStep('code');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? t.auth.errorOtp);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetVerify = async () => {
    setLoading(true);
    setError('');
    try {
      await resetPassword(resetMethod === 'email' ? resetPhone.trim() : normalizePhoneInput(resetPhone), resetCode, resetPasswordValue);
      setError('');
      // Regresar al login con éxito
      setStep('password');
      if (resetMethod === 'telegram') setPwPhone(normalizePhoneInput(resetPhone));
      setPassword('');
      setResetStep('method');
      setResetPhone('');
      setResetCode('');
      setResetPasswordValue(''); setResetConfirmValue('');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? t.auth.errorOtp);
    } finally {
      setLoading(false);
    }
  };

  // Show tenant error if config failed to load
  if (tenantError) {
    return (
      <div className={s.root}>
        <div className={s.card}>
          {/* Logo — even on error, show tenant branding if available */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            {tenantConfig?.logoUrl ? (
              <img
                src={tenantConfig.logoUrl}
                alt={tenantConfig.displayName}
                style={{ width: '56px', height: '56px', borderRadius: '4px', objectFit: 'cover' }}
              />
            ) : (
              <img src="/agrovid-logo.svg" alt="Agrovid" style={{ height: '56px', width: 'auto' }} />
            )}
            <Text size={600} weight="semibold" className={s.title}>
              {tenantConfig?.displayName || t.auth.title}
            </Text>
          </div>
          <Text size={500} weight="semibold" style={{ textAlign: 'center', color: tokens.colorStatusDangerForeground1 }}>
            {t.auth.tenantNotFound}
          </Text>
          <Text size={300} style={{ textAlign: 'center' }}>
            {t.auth.tenantNotFoundHint}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div className={s.card}>
        {/* Logo — show tenant logo if available */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          {tenantConfig?.logoUrl ? (
            <img
              src={tenantConfig.logoUrl}
              alt={tenantConfig.displayName}
              style={{ width: '56px', height: '56px', borderRadius: '4px', objectFit: 'cover' }}
            />
          ) : (
            <img src="/agrovid-logo.svg" alt="Agrovid" style={{ height: '56px', width: 'auto' }} />
          )}
        </div>
        <Text size={300} style={{ textAlign: 'center', color: tokens.colorNeutralForeground3, marginTop: '-8px' }}>
          {t.auth.subtitle}
        </Text>

        {step === 'google' && (
          <>
            <Text size={200} style={{ textAlign: 'center' }}>
              {t.auth.loginGoogle}
            </Text>
            {loading ? <Spinner size="small" /> : (
              <>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <GoogleSignInButton
                    onCredential={handleGoogle}
                    locale={locale}
                    renderKey={googleRetryKey}
                  />
                </div>
                {googleRejected && (
                  <Button
                    appearance="outline"
                    size="small"
                    onClick={() => {
                      googleDisableAutoSelect();
                      setGoogleRejected(false);
                      setError('');
                      setGoogleRetryKey((k) => k + 1);
                    }}
                  >
                    {t.auth.googleUseOtherAccount}
                  </Button>
                )}
                <Divider>o</Divider>
                <Text size={200} style={{ textAlign: 'center' }}>
                  {t.auth.emailPasswordTitle}
                </Text>
                <Input
                  type="email"
                  value={loginEmail}
                  onChange={(_, d) => setLoginEmail(d.value)}
                  placeholder={t.auth.emailLoginPlaceholder}
                  aria-label={t.auth.emailLoginPlaceholder}
                  autoComplete="username"
                  disabled={loading}
                />
                <PasswordField
                  value={loginEmailPassword}
                  onChange={setLoginEmailPassword}
                  placeholder={t.auth.passwordPlaceholder}
                  ariaLabel={t.auth.passwordPlaceholder}
                  autoComplete="current-password"
                  disabled={loading}
                  onEnter={handleEmailLogin}
                />
                <Button
                  appearance="primary"
                  onClick={handleEmailLogin}
                  disabled={loading || !isValidLoginEmail(loginEmail) || !loginEmailPassword}
                >
                  {t.auth.enter}
                </Button>
                <Divider>o</Divider>
                <Button
                  appearance="subtle"
                  size="small"
                  style={{ color: tokens.colorNeutralForeground3 }}
                  onClick={() => { setStep('password'); setError(''); }}
                >
                  {t.auth.hasPasswordLink}
                </Button>
                {tenantConfig?.demoEnabled === true && (
                  <>
                    <Divider />
                    <Button
                      appearance="outline"
                      style={{ borderStyle: 'dashed', color: tokens.colorNeutralForeground3 }}
                      onClick={handleDemo}
                    >
                      {t.auth.demoBtn}
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        )}

        {step === 'otp' && (
          <>
            <Text size={200} style={{ textAlign: 'center' }}>
              {t.auth.otpSent} ({phone})
            </Text>
            <div className={s.otpRow}>
              <Input
                value={otp}
                onChange={(_, d) => setOtp(d.value)}
                placeholder={t.auth.otpPlaceholder}
                style={{ flex: 1 }}
                maxLength={6}
                onKeyDown={(e) => e.key === 'Enter' && handleOtp()}
              />
              <Button appearance="primary" onClick={handleOtp} disabled={loading || otp.length !== 6}>
                {loading ? <Spinner size="tiny" /> : t.auth.verify}
              </Button>
            </div>
            <Button appearance="subtle" size="small" onClick={() => { setStep('google'); setOtp(''); setError(''); }}>
              {t.auth.back}
            </Button>
          </>
        )}

        {step === 'password' && (
          <>
            <Text size={200} style={{ textAlign: 'center' }}>
              {t.auth.passwordSubtitle}
            </Text>
            <PhoneInput
              value={pwPhone}
              onChange={setPwPhone}
              name="agrovid-login-phone"
              autoComplete="username"
              disabled={loading}
            />
            <PasswordField
              value={password}
              onChange={setPassword}
              placeholder={t.auth.passwordPlaceholder}
              ariaLabel={t.auth.passwordPlaceholder}
              autoComplete="current-password"
              disabled={loading}
              onEnter={handlePasswordLogin}
            />
            <Button
              appearance="primary"
              onClick={handlePasswordLogin}
              disabled={loading || !isValidPhoneE164(pwPhone) || !password}
            >
              {loading ? <Spinner size="tiny" /> : t.auth.enter}
            </Button>
            <Button
              appearance="subtle"
              size="small"
              onClick={() => { setStep('password-reset'); setResetStep('method'); setError(''); }}
            >
              {t.auth.forgotPassword}
            </Button>
            <Button appearance="subtle" size="small" onClick={() => { setStep('google'); setPassword(''); setError(''); }}>
              {t.auth.back}
            </Button>
          </>
        )}

        {step === 'password-reset' && (
          <>
            {resetStep === 'method' && (
              <>
                <Text size={200} style={{ textAlign: 'center' }}>
                  {t.auth.passwordResetSubtitle}
                </Text>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    appearance={resetMethod === 'telegram' ? 'primary' : 'secondary'}
                    onClick={() => changeResetMethod('telegram')}
                    disabled={loading}
                  >
                    {t.auth.resetViaTelegram}
                  </Button>
                  <Button
                    appearance={resetMethod === 'email' ? 'primary' : 'secondary'}
                    onClick={() => changeResetMethod('email')}
                    disabled={loading}
                  >
                    {t.auth.resetViaEmail}
                  </Button>
                </div>
                {resetMethod === 'telegram' ? (
                  <PhoneInput
                    key="reset-phone"
                    value={resetPhone}
                    onChange={setResetPhone}
                    name="agrovid-reset-phone"
                    autoComplete="off"
                    disabled={loading}
                    onEnter={() => resetValid && handlePasswordResetRequest()}
                  />
                ) : (
                  <Input
                    key="reset-email"
                    value={resetPhone}
                    onChange={(_, d) => setResetPhone(d.value)}
                    onBlur={() => setResetTouched(true)}
                    placeholder={t.auth.resetEmailPlaceholder}
                    type="email"
                    inputMode="email"
                    name="agrovid-reset-email"
                    autoComplete="off"
                    disabled={loading}
                    onKeyDown={(e) => e.key === 'Enter' && resetValid && handlePasswordResetRequest()}
                  />
                )}
                {resetMethod === 'email' && resetTouched && resetPhone && !resetValid && (
                  <Text size={200} style={{ color: tokens.colorStatusDangerForeground1 }}>
                    {t.auth.emailInvalid}
                  </Text>
                )}
                <Button
                  appearance="primary"
                  onClick={handlePasswordResetRequest}
                  disabled={loading || !resetValid}
                >
                  {loading ? <Spinner size="tiny" /> : t.auth.sendCode}
                </Button>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => { setStep('password'); setResetPhone(''); setError(''); }}
                >
                  {t.auth.back}
                </Button>
              </>
            )}

            {resetStep === 'code' && (
              <>
                <Text size={200} style={{ textAlign: 'center' }}>
                  {t.auth.resetCodeSentHint} {t.auth.enterResetCode}
                </Text>
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => {
                    changeResetMethod(resetMethod === 'telegram' ? 'email' : 'telegram');
                    setResetMethod(resetMethod === 'telegram' ? 'email' : 'telegram');
                    setResetPhone('');
                    setResetCode('');
                    setResetPasswordValue(''); setResetConfirmValue('');
                    setResetStep('method');
                    setResendIn(0); setResetLocked(false);
                  }}
                >
                  {t.auth.resetNotArrived}
                </Button>
                {resetLocked ? (
                  <Text size={200} role="alert" style={{ textAlign: 'center', color: tokens.colorStatusDangerForeground1 }}>
                    {t.auth.resetLocked}
                  </Text>
                ) : (
                  <Button
                    appearance="outline"
                    size="small"
                    onClick={handlePasswordResetRequest}
                    disabled={loading || resendIn > 0}
                  >
                    {resendIn > 0 ? t.auth.resendIn.replace('{n}', String(resendIn)) : t.auth.resendCode}
                  </Button>
                )}
                <Input
                  value={resetCode}
                  onChange={(_, d) => setResetCode(d.value)}
                  placeholder={t.auth.otpPlaceholder}
                  maxLength={6}
                  name="agrovid-reset-code"
                  id="agrovid-reset-code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  disabled={loading}
                  onKeyDown={(e) => e.key === 'Enter' && resetCode.length === 6 && resetPasswordOk && handlePasswordResetVerify()}
                />
                <NewPasswordFields
                  password={resetPasswordValue}
                  confirm={resetConfirmValue}
                  onPasswordChange={setResetPasswordValue}
                  onConfirmChange={setResetConfirmValue}
                  passwordPlaceholder={t.auth.newPasswordPlaceholder}
                  passwordLabel={t.auth.newPasswordPlaceholder}
                  disabled={loading}
                  minLength={tenantConfig?.passwordMinLength}
                />
                <Button
                  appearance="primary"
                  onClick={handlePasswordResetVerify}
                  disabled={loading || resetCode.length !== 6 || !resetPasswordOk}
                >
                  {loading ? <Spinner size="tiny" /> : t.auth.resetPassword}
                </Button>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => { setResetStep('method'); setResetCode(''); setResetPasswordValue(''); setResetConfirmValue(''); setError(''); }}
                >
                  {t.auth.back}
                </Button>
              </>
            )}
          </>
        )}

        {(otpLocked || otpWaitIn > 0) && (
          <div className={s.error} role="alert">
            {otpLocked ? t.auth.otpLocked : t.auth.otpWait.replace('{n}', String(otpWaitIn))}
          </div>
        )}
        {error && <div className={s.error}>{error}</div>}
      </div>

      <div style={{ display: 'flex', gap: '6px', marginTop: '16px' }}>
        {LOCALE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => { localeTouchedRef.current = true; setLocale(opt.value as Locale); }}
            aria-label={opt.label}
            aria-pressed={locale === opt.value}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '4px 10px', borderRadius: '999px', cursor: 'pointer',
              fontSize: '12px', fontWeight: locale === opt.value ? 600 : 400,
              border: `1px solid ${locale === opt.value ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
              background: locale === opt.value ? tokens.colorBrandBackground2 : 'transparent',
              color: locale === opt.value ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3,
            }}
          >
            <Flag iso={opt.flagIso} />
            <span>{opt.value.toUpperCase()}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
